import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AuthForm from "../components/AuthForm";
import InputField from "../components/InputField";
import Button from "../components/Button";
import { showToast } from "../components/ToastNotification";

const PasswordReset = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState("");

    useEffect(() => {
        // Extract token from URL parameters
        const accessToken = searchParams.get('access_token');
        
        if (!accessToken) {
            showToast("Invalid or missing reset token", "error");
            navigate("/auth/signin");
            return;
        }
        
        setToken(accessToken);
    }, [searchParams, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!password) {
            showToast("Please enter a new password", "error");
            return;
        }

        if (password.length < 6) {
            showToast("Password must be at least 6 characters long", "error");
            return;
        }

        if (password !== confirmPassword) {
            showToast("Passwords do not match", "error");
            return;
        }

        setLoading(true);
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/password-reset`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    password: password,
                    access_token: token 
                }),
            });

            const data = await response.json();

            if (response.ok) {
                showToast("Password updated successfully! Please sign in with your new password.", "success");
                navigate("/auth/signin");
            } else {
                showToast(data.detail || "Failed to update password", "error");
            }
        } catch (error) {
            console.error("Password reset error:", error);
            showToast("Network error. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <AuthForm title="Invalid Reset Link">
                <p className="text-center text-gray-600 mb-4">
                    This password reset link is invalid or has expired.
                </p>
                <Button 
                    text="Request New Reset Link" 
                    fullWidth 
                    onClick={() => navigate("/auth/password-recovery")}
                />
            </AuthForm>
        );
    }

    return (
        <AuthForm title="Set New Password">
            <form onSubmit={handleSubmit}>
                <InputField
                    label="New Password"
                    type="password"
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                />
                <InputField
                    label="Confirm New Password"
                    type="password"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                />
                <Button 
                    text={loading ? "Updating..." : "Update Password"} 
                    fullWidth 
                    disabled={loading}
                    type="submit"
                />
            </form>
        </AuthForm>
    );
};

export default PasswordReset;